import { Provider } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import type { ErrorInfo, ReactElement, ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  ApolloProvider,
} from '@apollo/client'
import { OptimizelyProvider } from '@optimizely/react-sdk'
import type { ReactSDKClient } from '@optimizely/react-sdk'
import { Map } from 'immutable'
import type { History } from 'history'
import type { Store, UnknownAction } from 'redux'
import _get from 'lodash/get'
import { BOOTSTRAP_PHASE_COMPLETE, BOOTSTRAP_PHASE_INIT } from '../../services/app/index'
import HistoryContext from '../Router/HistoryContext'
import { setAppBootstrapPhase } from '../../services/app/actions'
import headerRoutes from '../../routes/header-routes'
import footerRoutes from '../../routes/footer-routes'
import FooterApp from '../FooterApp/index'
import HeaderApp from '../HeaderApp/index'
import mainRoutes from '../../routes/routes'
import Router from '../Router/index'
import Head from '../Head/index'
import Main from '../Main/index'
import { H1, HEADING_TYPES } from '../Heading/index'
import { addNewRelicAction } from '../../services/app/_newrelic'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { getOptimizelyFeatureClient, getOptimizelyFeatureUser } from '../../services/optimizely-feature/index'
import { client as apolloClient } from '../../services/apollo-graphql/index'
import { get as getConfig } from '../../config/index'
import { GaiaPostHogProvider } from '../PostHog/index'
import { GaiaBrazeProvider } from '../Braze/index'
import {
  HydrationContext,
  HydrationManager,
} from './Hydration'
import HydrationTimingBoundary from './HydrationTimingBoundary'
import { DocumentHeadProvider } from '../DocumentHead'
import type { DocumentHeadRequestContext } from '../DocumentHead'

/**
 * Head state collected for a single server render. The document head is a
 * universal SSR + client-navigation contract: on the server each request owns
 * one of these and the middleware serializes it after the render completes; in
 * the browser there is no context and the current renderer writes to document
 * instead. See domain/server.md.
 */
type StateMap = Map<string, unknown>

/** The slices App itself reads; the store holds many more. */
interface AppReduxState {
  auth: StateMap
  optimizelyFeature: StateMap
  resolver: StateMap
  staticText: StateMap
  user: StateMap
}

export type AppStore = Store<AppReduxState, UnknownAction>

interface AppProps {
  history: History
  store: AppStore
  /** Server only — omitted in the browser so DocumentHead manages the live document. */
  documentHeadRequestContext?: DocumentHeadRequestContext
}

interface OptimizelyUser {
  id: string | null
  attributes?: Record<string, string | number | boolean | null>
}

/**
 * The failure domains an error can be attributed to: the shared provider stack
 * plus each high-level slot. These are fixed tokens — never a component name or
 * any per-request value — so New Relic groups errors by boundary.
 */
export const APP_ERROR_BOUNDARIES = {
  root: 'root',
  head: 'head',
  header: 'header',
  page: 'page',
  footer: 'footer',
} as const

export type AppErrorBoundaryName =
  typeof APP_ERROR_BOUNDARIES[keyof typeof APP_ERROR_BOUNDARIES]

interface FallbackOptions {
  message: string
  title: string
}

function renderFallback (options: FallbackOptions) {
  const { message, title } = options

  return (
    <div className="app__error" role="alert">
      <div className="app__logo">
        <svg width="81" height="29" xmlns="http://www.w3.org/2000/svg"><path d="M18.508 8.369c0 1.808 1.373 3.122 3.265 3.122 1.893 0 3.232-1.213 3.232-3.017a3.416 3.416 0 00-.726-2.099 9.598 9.598 0 00-4.075-3.064 13.304 13.304 0 00-6.09-.839c-.382.03-.764.067-1.141.117C5.418 3.53 0 8.32 0 16.246c0 8.67 7.554 12.352 15.193 12.352 3.328 0 7.923-.878 9.59-1.839V15.633H14.362v.84l3.504.613v9.774c-.482.042-.965.088-1.402.088-5.913 0-8.146-5.56-8.146-11.516 0-4.726 1.86-8.6 4.533-10.379a6.333 6.333 0 012.883-1.04 8.495 8.495 0 013.912.532c.286.109.566.234.839.374.289.152.57.32.84.503a2.938 2.938 0 00-2.817 2.947zm35.699-1.343c2.237 0 3.861-1.47 3.861-3.496 0-2.028-1.679-3.53-3.861-3.53-2.182 0-3.752 1.486-3.752 3.53 0 2.043 1.544 3.496 3.752 3.496zm-9.14 18.961V14.874c0-4.197-2.846-6.585-7.811-6.585-.995 0-6.077.155-8.465 3.231a3.206 3.206 0 00-.672 1.986c0 1.678 1.221 2.837 3.035 2.837 1.813 0 3.067-1.234 3.067-2.938a2.745 2.745 0 00-2.652-2.787 8.394 8.394 0 013.244-.608h.298c2.149.063 3.383 1.527 3.383 4.012l.03 3.714h-1.68c-6.119 0-9.92 2.25-9.92 5.876 0 3.441 2.735 4.981 5.455 4.981 3.206 0 5.809-2.669 6.24-3.139l.983 2.804h7.932v-1.075l-2.468-1.196zm-8.134-1.272c-1.47 0-3.043-.654-3.043-2.492 0-2.573 2.195-2.938 3.508-2.938l1.1-.03v5.242a5.275 5.275 0 01-1.565.218zm20.35-16.422h-2.812l-6.878 2.015v1.024h3.168v15.486l-2.199.55v.898h11.009v-.944l-2.283-.592-.005-18.437zM80.727 27.18l-2.43-1.196v-11.11c0-4.196-2.845-6.584-7.81-6.584-.995 0-6.077.155-8.465 3.231a3.21 3.21 0 00-.684 1.986c0 1.678 1.22 2.837 3.034 2.837 1.813 0 3.068-1.234 3.068-2.938a2.745 2.745 0 00-2.653-2.787 8.394 8.394 0 013.245-.608h.298c2.148.063 3.382 1.527 3.382 4.012l.03 3.714h-1.68c-6.118 0-9.92 2.25-9.92 5.876 0 3.441 2.736 4.981 5.455 4.981 3.207 0 5.809-2.669 6.241-3.139l.982 2.804h7.932l-.025-1.079zM70.13 24.715c-1.469 0-3.043-.654-3.043-2.492 0-2.573 2.195-2.938 3.505-2.938l1.1-.03v5.242a5.271 5.271 0 01-1.562.218z" fill="#00C9C9" /></svg>
      </div>
      <H1 as={HEADING_TYPES.H2} className="app__error-title">
        {title}
      </H1>
      <p>{message}</p>
    </div>
  )
}

function logError (
  error: Error,
  info: ErrorInfo,
  store: AppStore,
  boundary: AppErrorBoundaryName,
) {
  const { message } = error
  const { componentStack } = info
  const state = store.getState()
  const { auth, user, resolver } = state
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const pageUrl = resolver.get('path') || global.location.pathname
  const dataNewRelic = {
    boundary,
    errorMessage: message,
    componentStack,
    isLoggedIn,
    anonymousUuid: user.getIn(['data', 'anonymousUuid']),
    country: auth.get('country'),
    username: auth.get('username'),
    pageUrl,
  }

  addNewRelicAction('AppErrorBoundary', dataNewRelic)
}

interface AppErrorBoundaryProps {
  boundary: AppErrorBoundaryName
  children: ReactNode
  /** What replaces the subtree once it has thrown; nothing by default. */
  fallback?: ReactElement | null
  store: AppStore
}

/**
 * One failure domain. A subtree that throws is replaced by its own fallback —
 * nothing for a chrome slot, the localized app error for the page — leaving its
 * siblings mounted, and every catch is reported under a fixed boundary name.
 */
function AppErrorBoundary (props: AppErrorBoundaryProps) {
  const {
    boundary,
    children,
    fallback = null,
    store,
  } = props

  return (
    <ErrorBoundary
      fallback={fallback}
      onError={(error, info) => logError(error, info, store, boundary)}
    >
      {children}
    </ErrorBoundary>
  )
}

interface OptimizelyProviderWrapperProps {
  state: AppReduxState
  children: ReactNode
}

// Optimizely Feature Testing: adding OptimizelyProvider
const OptimizelyProviderWrapper = ({ state, children }: OptimizelyProviderWrapperProps) => {
  const { optimizelyFeature } = state
  const config = getConfig()
  const optimizelyFeatureTesting = !!_get(config, ['features', 'optimizelyFeatureTesting'])

  const rawUser = optimizelyFeature.get('user')
  // The SDK reads `id` and `attributes` as plain properties and resolves its user
  // promise as failed — permanently, for the life of the client — when `id` is
  // missing. Redux holds the user as an Immutable Map, so it must be converted.
  const stateUser = Map.isMap(rawUser) ? (rawUser as StateMap) : undefined
  const storedUser = stateUser && stateUser.get('id')
    ? (stateUser.toJS() as OptimizelyUser)
    : undefined
  const user = storedUser || (getOptimizelyFeatureUser(state) as OptimizelyUser | undefined)
  const optimizely = getOptimizelyFeatureClient() as ReactSDKClient | undefined

  if (optimizelyFeatureTesting && user && optimizely) {
    return (
      <OptimizelyProvider optimizely={optimizely} user={user}>
        {children}
      </OptimizelyProvider>
    )
  }

  return <>{children}</>
}

function App (props: AppProps) {
  const {
    history,
    store,
    documentHeadRequestContext,
  } = props
  const state = store.getState()
  const { staticText } = state
  const appStaticText: Map<string, string> = staticText.getIn(['data', 'app', 'data'])
  // The one localized app error, shared by the boundaries that own a surface a
  // user reads the page off: the provider stack and the page itself.
  const appErrorFallback = renderFallback({
    message: appStaticText.get('errorMessage'),
    title: appStaticText.get('errorTitle'),
  })

  // keep a local state for hydrated boolean.
  // there is no getting around this since App
  // is rendered before the redux Provider, which means
  // App will not re-render when redux state is modified.
  const [hydrated, setHydrated] = useState(false)

  // create a cached hydrator object, which we will use
  // as our context to the Hydration.Provider
  const hydrator = useMemo(() => (
    HydrationManager.getContextAPI({
      onHydrationComplete: () => setHydrated(true),
    })
  ), [hydrated])

  // when App mounts, kick off the INIT phase.
  // if the hydrator is empty by the time App has mounted,
  // it means there are no async components (Suspense/Lazy) on the page
  // and we are safe to go ahead and mark hydration as complete, which will
  // then trigger the second useEffect below, settings the phase to COMPLETE.
  useEffect(() => {
    store.dispatch(setAppBootstrapPhase(
      BOOTSTRAP_PHASE_INIT,
    ))
    if (hydrator.empty()) {
      hydrator.setComplete(true)
    }
  }, [])

  // when App re-renders and hydrate is now 'true',
  // it means React's reconsiliation of async, Suspense/Lazy components
  // has finished and we are safe to tell the App bootstrapping is 'complete'
  // which will load the client-side specific UI and interactions
  // that are not supported on the server.
  useEffect(() => {
    if (hydrated) {
      store.dispatch(setAppBootstrapPhase(
        BOOTSTRAP_PHASE_COMPLETE,
      ))
    }
  }, [hydrated])

  return (
    // Head state is scoped to this provider. The server passes a per-request
    // context to read after rendering; the browser passes none.
    <DocumentHeadProvider requestContext={documentHeadRequestContext}>
      {/*
        The last boundary: a failure in the shared providers leaves no slot to
        fall back to, so it renders the localized app error for the whole page.
      */}
      <AppErrorBoundary
        boundary={APP_ERROR_BOUNDARIES.root}
        fallback={appErrorFallback}
        store={store}
      >
        <ApolloProvider client={apolloClient}>
          <Provider store={store}>
            <GaiaPostHogProvider>
              <GaiaBrazeProvider>
                <HydrationContext.Provider value={hydrator}>
                  <HistoryContext.Provider value={history}>
                    <OptimizelyProviderWrapper state={state}>
                      {/*
                        Four independent slots. Each owns its failure, so one
                        throwing subtree costs the user that slot rather than
                        the page — a slot only reports readiness if it mounted.
                      */}
                      <AppErrorBoundary boundary={APP_ERROR_BOUNDARIES.head} store={store}>
                        <HydrationTimingBoundary milestone="head">
                          <Head />
                        </HydrationTimingBoundary>
                      </AppErrorBoundary>
                      <AppErrorBoundary boundary={APP_ERROR_BOUNDARIES.header} store={store}>
                        <HydrationTimingBoundary milestone="header">
                          <HeaderApp>
                            <Router routes={headerRoutes} />
                          </HeaderApp>
                        </HydrationTimingBoundary>
                      </AppErrorBoundary>
                      <AppErrorBoundary
                        boundary={APP_ERROR_BOUNDARIES.page}
                        fallback={appErrorFallback}
                        store={store}
                      >
                        <HydrationTimingBoundary milestone="page">
                          <Main history={history} client={apolloClient}>
                            <Router routes={mainRoutes} />
                          </Main>
                        </HydrationTimingBoundary>
                      </AppErrorBoundary>
                      <AppErrorBoundary boundary={APP_ERROR_BOUNDARIES.footer} store={store}>
                        <HydrationTimingBoundary milestone="footer">
                          <FooterApp>
                            <Router routes={footerRoutes} />
                          </FooterApp>
                        </HydrationTimingBoundary>
                      </AppErrorBoundary>
                    </OptimizelyProviderWrapper>
                  </HistoryContext.Provider>
                </HydrationContext.Provider>
              </GaiaBrazeProvider>
            </GaiaPostHogProvider>
          </Provider>
        </ApolloProvider>
      </AppErrorBoundary>
    </DocumentHeadProvider>
  )
}

export default App
